import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import { Home, TrendingUp, HardHat, Camera, Calendar, Users, Globe, LogOut, MoreHorizontal, CreditCard, Gift, BookUser, FileCheck, Clock, ChevronDown, UserCog, Palette, RefreshCw, Plug, Activity, Sparkles, Images, Settings, HelpCircle, BarChart3, Plus, Truck, Bell } from "lucide-react";
import AssistantPanel from "@/components/AssistantPanel";
import LanguageToggle from "@/components/LanguageToggle";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect, useRef, useState } from "react";
import { api, API, formatApiError, getToken, setToken } from "@/lib/api";
import { getProjects, getBillingStatus, getProfile } from "@/lib/refData";
import { startAutoFlush, getQueued, migrateQueue } from "@/lib/offlineQueue";
import { loadUploadPrefs } from "@/lib/imagePrep";
import { flushWalkthroughs, recoverInterruptedWalkthroughs } from "@/lib/walkthroughStore";
import { recoverLegacyQueues } from "@/lib/legacyRecovery";
import { getFieldMode, subscribeFieldMode } from "@/lib/fieldMode";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import InstallPrompt, { isInstalled } from "@/components/InstallPrompt";
import UploadQueuePill from "@/components/UploadQueuePill";
import QuickAddSheet from "@/components/QuickAddSheet";
import { getPositionOnce, nearestJob, distM } from "@/lib/nearbyJob";
import UpdateToast from "@/components/UpdateToast";
import PushPrompt from "@/components/PushPrompt";
import FieldModeToggle from "@/components/FieldModeToggle";
import { LogoIcon, LogoWordmark } from "@/components/Logo";
import CameraCapture from "@/components/CameraCapture";
import FileViewer from "@/components/FileViewer";
import { useModalBack, onModalStackChange, setDiscardHandler } from "@/lib/modalBack";
import { localDayKey } from "@/lib/format";
import CcAnnouncementBanner from "@/components/cc/CcAnnouncementBanner";

// Primary nav (spec: Home, Projects, Camera, Photos, Schedule, More)
const mainItems = [
  { to: "/app", key: "nav_home", icon: Home, testid: "nav-home", end: true },
  { to: "/app/projects", key: "nav_projects", icon: HardHat, testid: "nav-projects" },
  { camera: true, key: "nav_camera", icon: Camera, testid: "nav-camera", mod: "photo_uploads" },
  { to: "/app/photos", key: "nav_photos", icon: Images, testid: "nav-photos", mod: "photo_uploads" },
  { to: "/app/schedule", key: "nav_schedule", icon: Calendar, testid: "nav-schedule" },
  { to: "/app/financials", key: "nav_financials", icon: FileCheck, testid: "nav-money", finOnly: true, mod: "invoicing_estimates" },
];

// Field Mode nav: My Day replaces Home
const fieldItems = [
  { to: "/app/time", key: "nav_my_day", icon: Clock, testid: "nav-my-day", mod: "timeclock_gps", end: true },
  { to: "/app/projects", key: "nav_projects", icon: HardHat, testid: "nav-projects" },
  { to: "/app/schedule", key: "nav_schedule", icon: Calendar, testid: "nav-schedule" },
  { to: "/app/financials", key: "nav_financials", icon: FileCheck, testid: "nav-money-field", finOnly: true, mod: "invoicing_estimates" },
];

// More menu, grouped (spec order) — pre-launch cleanup (James B): 7 entries.
// Money (cost reports live inside Money), Customers (Leads as a tab there),
// Time & Crew merged, Website, Help, Refer, Settings (billing/updates/assistant
// activity live inside Settings). Integrations hidden until a provider ships.
const moreGroups = [
  { key: "nav_group_business", items: [
    { to: "/app/customers", key: "nav_customers", icon: BookUser, testid: "more-customers-desk", desktopOnly: true },
  ]},
  { key: "nav_group_team", items: [
    { to: "/app/time", key: "nav_time_crew", icon: Clock, testid: "more-time", desktopOnly: true },
    { to: "/app/fleet", key: "nav_fleet", icon: Truck, testid: "more-fleet" },
    { to: "/app/subs", key: "nav_subs", icon: HardHat, testid: "more-subs", ownerOnly: true },
  ]},
  { key: "nav_group_company", items: [
    { to: "/app/website", key: "nav_website", icon: Globe, testid: "more-website", ownerOnly: true },
  ], hideInField: true },
  { key: "nav_group_tools", items: [
    { to: "/app/help", key: "nav_help", icon: HelpCircle, testid: "more-help" },
    { to: "/app/referrals", key: "nav_referrals", icon: Gift, testid: "more-referrals", ownerOnly: true },
    { to: "/app/settings", key: "nav_settings", icon: Settings, testid: "more-settings", ownerOnly: true },
  ], hideInField: true },
];

// Mobile More sheet adds Customers/Estimates & Invoices/My Day (Photos now lives in main nav)
const mobileWorkItems = [
  { to: "/app/customers", key: "nav_customers", icon: BookUser, testid: "more-customers" },
  { to: "/app/time", key: "nav_time", icon: Clock, testid: "more-time", mod: "timeclock_gps", exact: true },
];

export default function AppShell({ children }) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [billingAlert, setBillingAlert] = useState(null);
  const [fileView, setFileView] = useState(null); // global in-app file viewer (No Dead Ends)
  const [anyModal, setAnyModal] = useState(false);
  useEffect(() => onModalStackChange(setAnyModal), []); // hide bottom nav while any sheet/modal is open (Round 12 #2)
  const camReturnTo = useRef("/app/photos"); // camera Done returns where it started
  const [billingCanRequest, setBillingCanRequest] = useState(false);
  const [billingReqPending, setBillingReqPending] = useState(false);
  const [billingReqBusy, setBillingReqBusy] = useState(false);
  const [trialDays, setTrialDays] = useState(null);
  const [myPhoto, setMyPhoto] = useState(null);
  const [moreOpen, setMoreOpen] = useState(false);
  useModalBack(moreOpen, () => setMoreOpen(false)); // phone Back closes the More sheet (Round 11 #1)
  const [quickAdd, setQuickAdd] = useState(false); // Batch 1: big + button sheet
  const [deskMoreOpen, setDeskMoreOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  useModalBack(profileOpen, () => setProfileOpen(false)); // phone Back closes the account menu (Round 11 #1)
  // Notifications bell: unread badge (60s poll), dropdown feed, mark-all-read
  const [bellOpen, setBellOpen] = useState(false);
  useModalBack(bellOpen, () => setBellOpen(false));
  const [notifs, setNotifs] = useState(null);
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    let stop = false;
    const load = () => api.get("/notifications/unread-count").then(({ data }) => { if (!stop) setUnread(data.count || 0); }).catch(() => {});
    load();
    const iv = setInterval(load, 60000);
    return () => { stop = true; clearInterval(iv); };
  }, []);
  const openBell = async () => {
    const next = !bellOpen;
    setBellOpen(next);
    if (next) {
      try { const { data } = await api.get("/notifications"); setNotifs(data.items || []); }
      catch { setNotifs([]); }
    }
  };
  const markAllRead = async () => {
    await api.post("/notifications/read-all").catch(() => {});
    setUnread(0);
    setNotifs((l) => (l || []).map((n) => ({ ...n, read: true })));
  };
  const tapNotif = (n) => {
    setBellOpen(false);
    navigate(n.url && n.url.startsWith("/app") ? n.url : "/app");
  };
  // One open of the installed PWA marks the user app_installed server-side —
  // every device and browser then stops ALL install reminders.
  useEffect(() => {
    if (!user || user.app_installed || !isInstalled()) return;
    try { if (localStorage.getItem("bf.installed.reported")) return; } catch { /* ignore */ }
    api.post("/auth/install-state", { installed: true })
      .then(() => { try { localStorage.setItem("bf.installed.reported", "1"); } catch { /* ignore */ } })
      .catch(() => {});
  }, [user]);
  const [camPickerOpen, setCamPickerOpen] = useState(false);
  useModalBack(camPickerOpen, () => setCamPickerOpen(false)); // phone Back closes the picker (Round 10 #1)
  const [camProject, setCamProject] = useState(null);
  const [camMode, setCamMode] = useState("photo");
  const [camProjects, setCamProjects] = useState([]);
  const [camTodayId, setCamTodayId] = useState(null); // today's scheduled job — camera prefers it
  const [camSearch, setCamSearch] = useState("");
  const [camLoading, setCamLoading] = useState(false);
  const [camNearIds, setCamNearIds] = useState(new Set()); // projects within ~200m GPS
  const [fieldMode, setFieldMode] = useState(getFieldMode);
  const [modVis, setModVis] = useState({});
  useEffect(() => {
    api.get("/tenant/settings").then(({ data }) => setModVis(data.modules_visibility || {})).catch(() => {});
    // E1: warm the 360 LLM pipeline at app open so the FIRST interpret is fast
    // (not just the ones after the panel has been opened once).
    try {
      if (!sessionStorage.getItem("bf.360warmed")) {
        sessionStorage.setItem("bf.360warmed", "1");
        api.get("/update360/warm").catch(() => {});
      }
      // Round 18 #3: keep the LLM connection alive — re-warm every 4 min while
      // the app is open+visible so the first interpret never goes cold again.
      const warmIv = setInterval(() => {
        if (document.visibilityState === "visible") api.get("/update360/warm").catch(() => {});
      }, 4 * 60 * 1000);
      return () => clearInterval(warmIv);
    } catch { /* ignore */ }
    return undefined;
  }, []);
  // Module visibility (Company Settings): hide nav entries per role
  const modOn = (k) => { const m = modVis[k]; return !m || m[user?.role] !== false; };
  const showMoneyNav = user?.role !== "crew" && modOn("invoicing_estimates");
  const [asstOpen, setAsstOpen] = useState(false);
  const [asstMounted, setAsstMounted] = useState(false);
  const [asstInit, setAsstInit] = useState(null);
  const openAssistant = () => { setAsstInit(null); setAsstMounted(true); setAsstOpen(true); };
  useEffect(() => {
    const onOpen = (e) => { setAsstInit(e.detail || null); setAsstMounted(true); setAsstOpen(true); };
    window.addEventListener("bf:open-assistant", onOpen);
    return () => window.removeEventListener("bf:open-assistant", onOpen);
  }, []);
  useEffect(() => {
    const onQA = () => setQuickAdd(true);
    window.addEventListener("bf:open-quick-add", onQA);
    return () => window.removeEventListener("bf:open-quick-add", onQA);
  }, []);

  const loc = useLocation();

  // Phase 6: consistent browser tab titles per route
  useEffect(() => {
    const p = loc.pathname;
    const map = [
      [/^\/app\/projects\/new/, "action_new_project"],
      [/^\/app\/projects/, "nav_projects"],
      [/^\/app\/photos/, "nav_photos"],
      [/^\/app\/camera/, "nav_camera"],
      [/^\/app\/schedule/, "nav_schedule"],
      [/^\/app\/customers/, "nav_customers"],
      [/^\/app\/leads/, "nav_leads"],
      [/^\/app\/financials/, "nav_financials"],
      [/^\/app\/reports/, "nav_reports"],
      [/^\/app\/more/, "nav_more"],
      [/^\/app\/time/, "nav_time"],
      [/^\/app\/profile/, "prof_title"],
      [/^\/app\/admin/, "admin_title"],
      [/^\/app/, "nav_home"],
    ];
    const hit = map.find(([re]) => re.test(p));
    document.title = hit ? `${t(hit[1])} | BuildProject360` : "BuildProject360";
  }, [loc.pathname, t]);

  // Round 11 #5: route-based tab titles for More-menu pages without their own useSeo.
  // IMPORTANT: this effect must run AFTER the Phase 6 effect above so its specific titles win
  // over the generic "nav_home" fallback.
  useEffect(() => {
    const MAP = [["/app/reports", "jr_title"], ["/app/crew", "nav_crew"], ["/app/website", "nav_website"],
      ["/app/help", "help_title"], ["/app/billing", "nav_plan_billing"], ["/app/referrals", "nav_referrals"],
      ["/app/assistant/activity", "aa_title"], ["/app/app-updates", "nav_app_updates"], ["/app/integrations", "nav_integrations"],
      ["/app/settings", "settings_title"]];
    const hit = MAP.find(([p]) => loc.pathname.startsWith(p));
    if (hit) document.title = `${t(hit[1])} | BuildProject360`;
  }, [loc.pathname, t]);

  // Phase 6: global offline indicator (photo uploads etc. queue and sync on reconnect)
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  useEffect(() => {
    const on = () => setIsOffline(false);
    const off = () => setIsOffline(true);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);
  const deskMoreRef = useRef(null);
  const profileRef = useRef(null);

  useEffect(() => subscribeFieldMode(setFieldMode), []);

  useEffect(() => {
    const close = (e) => {
      if (deskMoreRef.current && !deskMoreRef.current.contains(e.target)) setDeskMoreOpen(false);
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  // More is active only for routes NOT owned by a visible main-nav tab. /app/time
  // (My Day) and /app/financials (Money) have their own tabs — /app/timecards does not.
  const moreActive = [...moreGroups.flatMap((g) => g.items).filter((i) => !i.desktopOnly), ...mobileWorkItems]
    .some((i) => i.exact ? loc.pathname === i.to : loc.pathname.startsWith(i.to))
    || loc.pathname.startsWith("/app/timecards")
    || loc.pathname.startsWith("/app/admin");

  const openCameraPicker = async (forcePicker = false) => {
    camReturnTo.current = loc.pathname + loc.search;
    setCamSearch("");
    setCamLoading(true);
    let list = [];
    try {
      const data = await getProjects();
      const act = data.filter((p) => p.status !== "done" && p.status !== "archived");
      list = act.length ? act : data;
    } catch (e) { list = []; }
    setCamProjects(list);
    // Auto-select priority: current project page → today's scheduled job →
    // GPS-near (≤200 m, only when permission was already granted — never
    // prompt-block a nav tap) → last used. Picker only when none apply.
    const pm = loc.pathname.match(/^\/app\/projects\/([^/]+)/);
    let autoId = pm && pm[1] !== "new" ? pm[1] : null;
    let todayId = null;
    try {
      const today = localDayKey();
      const { data: sched } = await api.get("/schedule", { params: { start: today, end: today, limit: 50 } });
      todayId = (sched.items || []).find((s) => s.project_id)?.project_id || null;
    } catch { /* best-effort */ }
    setCamTodayId(todayId);
    if (!autoId) autoId = todayId;
    if (!autoId && (user?.notif_prefs?.nearby_first) !== false) {
      // "You're here" (James): one-shot GPS, ~5 s max, silent fallback.
      try {
        const pos = await getPositionOnce();
        if (pos) {
          const near = list.filter((p) => p.lat != null && p.lng != null &&
            distM({ lat: pos.lat, lng: pos.lng }, { lat: p.lat, lng: p.lng }) <= 200);
          setCamNearIds(new Set(near.map((p) => p.id)));
          const hit = nearestJob(list, pos, 150);
          if (hit) autoId = hit.project.id;
        }
      } catch { /* ignore */ }
    }
    if (!autoId) { try { autoId = localStorage.getItem("buildflow.camera.lastProject"); } catch { /* ignore */ } }
    const autoProj = !forcePicker && autoId ? list.find((p) => p.id === autoId) : null;
    if (autoProj) {
      try { localStorage.setItem("buildflow.camera.lastProject", autoProj.id); } catch { /* ignore */ }
      setCamLoading(false);
      setCamProjectName(autoProj.name || "");
      setCamProject(autoProj.id); // straight to the viewfinder — no picker
      return;
    }
    // Nothing confident → searchable picker (today's job first, then most-recently used)
    let recent = [];
    try { recent = JSON.parse(localStorage.getItem("buildflow.camera.recentProjects") || "[]"); } catch { /* ignore */ }
    const rank = (p) => {
      if (p.id === todayId) return -1;
      const i = recent.indexOf(p.id);
      return i === -1 ? 999 : i;
    };
    setCamProjects([...list].sort((a, b) => rank(a) - rank(b) || (a.name || "").localeCompare(b.name || "")));
    setCamPickerOpen(true);
    setCamLoading(false);
    // "Near you" badges — best-effort GPS, ~200 m radius, never blocks the picker.
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((pos) => {
        const toRad = (d) => (d * Math.PI) / 180;
        const dist = (a, b) => {
          const R = 6371000;
          const dLat = toRad(b.lat - a.lat); const dLng = toRad(b.lng - a.lng);
          const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
          return 2 * R * Math.asin(Math.sqrt(h));
        };
        const near = new Set(list.filter((p) => p.lat != null && p.lng != null &&
          dist({ lat: pos.coords.latitude, lng: pos.coords.longitude }, { lat: p.lat, lng: p.lng }) <= 200).map((p) => p.id));
        setCamNearIds(near);
      }, () => {}, { timeout: 6000 });
    }
  };

  const [camProjectName, setCamProjectName] = useState("");
  useEffect(() => {
    const openCam = (e) => {
      const pid = e?.detail?.projectId;
      if (pid) {
        // Caller already knows the project — straight to the viewfinder, no picker.
        setCamProjectName(e.detail.projectName || "");
        setCamMode(e.detail.mode || "photo"); // P0-3: always Photo unless explicitly Walkthrough
        try { localStorage.setItem("buildflow.camera.lastProject", pid); } catch { /* ignore */ }
        setCamProject(pid);
        return;
      }
      openCameraPicker(false);
    };
    const changeProj = () => openCameraPicker(true);
    window.addEventListener("bf:open-camera", openCam);
    window.addEventListener("bf:camera-change-project", changeProj);
    return () => {
      window.removeEventListener("bf:open-camera", openCam);
      window.removeEventListener("bf:camera-change-project", changeProj);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.pathname]);

  useEffect(() => {
    const onOpenFile = (e) => setFileView(e.detail || null);
    window.addEventListener("bf:open-file", onOpenFile);
    return () => window.removeEventListener("bf:open-file", onOpenFile);
  }, []);

  useEffect(() => {
    // Fast Photos (E): retries run on app open, foreground return, network
    // change and a 30s watchdog — never gated on the "online" event alone.
    startAutoFlush(api, (n) => toast.success(t("offline_synced", { count: n })));
    loadUploadPrefs();
    // P0-6: crash recovery — anything still in IndexedDB from a killed session
    // uploads/finishes by itself, and the user hears about it.
    migrateQueue().catch(() => ({ migrated: 0 })).then(() => getQueued({ includeUploaded: false })).then((its) => {
      const n = (its || []).filter((i) => i.kind === "photo" && ["pending", "uploading", "failed"].includes(i.status)).length;
      if (n > 0) toast(t("photos_recovered", { count: n }), { id: "photos-recovered", duration: 4000 });
    }).catch(() => {});
    // STOP-SHIP: also sweep OLD app versions' IndexedDB stores for photos that
    // never made it into the current queue (James's week of lost uploads).
    recoverLegacyQueues().then((n) => {
      if (n > 0) toast(t("photos_recovered", { count: n }), { id: "legacy-recovered", duration: 5000 });
    }).catch(() => {});
    recoverInterruptedWalkthroughs(api).then((n) => {
      if (n > 0) toast(t("wt_recovered", { count: n }), { id: "wt-recovered", duration: 4000 });
    }).catch(() => {});
    const flush = () => {
      flushWalkthroughs(api).catch(() => {});
    };
    if (navigator.onLine) flush();
    window.addEventListener("online", flush);
    // Walkthrough built while the user was offline/away → straight to review.
    const onWtReady = (e) => {
      const { sessionId, projectId } = e.detail || {};
      if (sessionId && projectId) {
        toast.success(t("wt_ready"), { duration: 6000 });
        navigate(`/app/projects/${projectId}?walkthrough=${sessionId}`);
      }
    };
    window.addEventListener("bf:walkthrough-ready", onWtReady);
    getBillingStatus().then((data) => {
      if (data.state === "grace") setBillingAlert(t("trial_banner_grace"));
      else if (data.state === "expired") setBillingAlert(t("trial_banner_expired"));
      else if (data.state === "suspended") setBillingAlert(t("trial_banner_suspended"));
      else if (data.subscription_status === "past_due") setBillingAlert(t("billing_banner_past_due"));
      else if (data.subscription_status === "canceled") setBillingAlert(t("billing_banner_canceled"));
      else if (data.state === "trialing" && data.trial_days_remaining != null) setTrialDays(data.trial_days_remaining);
      setBillingCanRequest(!!data.can_request_extension);
      setBillingReqPending(!!data.extension_request_pending);
    }).catch(() => {});
    getProfile().then((data) => setMyPhoto(data)).catch(() => {});
    return () => { window.removeEventListener("online", flush); };
  }, [t]);

  // Push deep link: /app?u360=<projectId|multi> opens the 360 on that job
  // ("multi" → the 360 asks "Which job?" with today's jobs as chips).
  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    const u360 = params.get("u360");
    if (!u360) return;
    window.dispatchEvent(new CustomEvent("bf:open-assistant", {
      detail: { mode: "update", projectId: u360 === "multi" ? null : u360, projectName: "" } }));
    params.delete("u360");
    navigate({ search: params.toString() ? `?${params.toString()}` : "" }, { replace: true });
  }, [loc.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const enterDemo = async () => {
    try {
      const { data } = await api.post("/auth/demo/enter");
      localStorage.setItem("bp360_return_token", getToken() || "");
      setToken(data.access_token);
      window.location.href = "/app";
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || t("toast_failed")); }
  };
  const exitDemo = () => {
    const back = localStorage.getItem("bp360_return_token");
    if (back) { localStorage.removeItem("bp360_return_token"); setToken(back); window.location.href = "/app"; }
  };
  const [demoResetting, setDemoResetting] = useState(false);
  const [demoConfirm, setDemoConfirm] = useState(false);
  const resetDemo = async () => {
    setDemoConfirm(false);
    setDemoResetting(true);
    try {
      await api.post("/auth/demo/reset");
      toast.success(t("demo_reset_done"));
      window.location.href = "/app";
    } catch (e) { setDemoResetting(false); toast.error(formatApiError(e.response?.data?.detail) || t("toast_failed")); }
  };
  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  const requestExtension = async () => {
    setBillingReqBusy(true);
    try {
      await api.post("/billing/request-extension", { message: "" });
      setBillingReqPending(true);
      toast.success(t("trial_request_sent"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setBillingReqBusy(false);
  };

  const initials = (user?.name || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  const visibleMain = mainItems.filter((it) => (!it.finOnly || user?.role !== "crew") && (!it.mod || modOn(it.mod)));

  return (
    <div className="min-h-screen bg-slate-50 bf-grain relative">
      {/* Top bar */}
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-2 px-4 py-3">
          <div className="flex min-w-0 items-center gap-2.5">
            <LogoIcon size={36} className="shrink-0" />
            <div className="min-w-0">
              <LogoWordmark className="text-sm" testid="logo-header" />
              <div className="text-[11px] text-slate-500 truncate max-w-[130px] sm:max-w-none">{user?.company_name}</div>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
            {/* Notifications bell — newest first, tap deep-links, mark all read */}
            <div className="relative">
              <button onClick={openBell} data-testid="notif-bell" aria-label={t("nb_title")} aria-expanded={bellOpen}
                className="relative flex h-11 w-11 min-h-[44px] min-w-[44px] items-center justify-center rounded-full border border-slate-300 bg-white text-slate-600 active:scale-95">
                <Bell className="h-5 w-5" />
                {unread > 0 && (
                  <span data-testid="notif-badge" className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-extrabold text-white">
                    {unread > 99 ? "99+" : unread}
                  </span>
                )}
              </button>
              {bellOpen && (
                <>
                <div className="fixed inset-0 z-40" onClick={() => setBellOpen(false)} data-testid="notif-backdrop" />
                <div className="fixed left-2 right-2 top-16 z-50 rounded-2xl border border-slate-200 bg-white shadow-xl sm:absolute sm:left-auto sm:right-0 sm:top-12 sm:w-80" data-testid="notif-dropdown">
                  <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
                    <span className="text-xs font-bold uppercase tracking-widest text-slate-500">{t("nb_title")}</span>
                    {unread > 0 && (
                      <button onClick={markAllRead} data-testid="notif-mark-all" className="min-h-[36px] text-xs font-bold text-brand">{t("nb_mark_all")}</button>
                    )}
                  </div>
                  <div className="max-h-[60vh] overflow-y-auto overscroll-contain">
                    {notifs === null ? (
                      <div className="p-4"><div className="bf-skeleton h-16" /></div>
                    ) : notifs.length === 0 ? (
                      <p className="p-4 text-sm text-slate-500" data-testid="notif-empty">{t("nb_empty")}</p>
                    ) : (
                      notifs.map((n) => (
                        <button key={n.id} onClick={() => tapNotif(n)} data-testid={`notif-${n.id}`}
                          className={`w-full border-b border-slate-50 px-4 py-3 text-left last:border-0 ${n.read ? "opacity-60" : ""}`}>
                          <div className="flex items-start gap-2.5">
                            {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-brand" data-testid={`notif-dot-${n.id}`} />}
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-slate-800">{n.title}</div>
                              {n.body ? <div className="mt-0.5 line-clamp-2 text-xs text-slate-500">{n.body}</div> : null}
                              <div className="mt-0.5 text-[10px] text-slate-400">
                                {n.created_at ? new Date(n.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : ""}
                              </div>
                            </div>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
                </>
              )}
            </div>
            {/* Global Project Assistant launcher — desktop label button, mobile icon button */}
            <button onClick={openAssistant} data-testid="assistant-launcher" aria-label={t("ap_title")}
              className="hidden sm:inline-flex min-h-[44px] items-center gap-1.5 rounded-full bg-brand px-4 text-xs font-bold text-white hover:bg-branddeep active:scale-95">
              <Sparkles className="h-4 w-4" /> {t("ap_launcher")}
            </button>
            <button onClick={openAssistant} data-testid="assistant-launcher-mobile" aria-label={t("ap_title")}
              className="flex sm:hidden h-11 w-11 min-h-[44px] min-w-[44px] items-center justify-center rounded-full bg-brand text-white active:scale-95">
              <Sparkles className="h-5 w-5" />
            </button>
            {user?.is_superadmin && (
              <button data-testid="btn-admin" onClick={() => navigate("/app/admin")}
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full border border-brand/50 bg-brand/10 px-3 text-xs font-bold text-brand hover:bg-brand/20 active:scale-95">
                Admin
              </button>
            )}
            {/* Profile menu: language, field mode, account, sign out */}
            <div className="relative" ref={profileRef}>
              <button onClick={() => setProfileOpen((v) => !v)} data-testid="profile-menu-btn"
                aria-label={t("profile_menu")} aria-expanded={profileOpen}
                className="flex h-11 w-11 min-h-[44px] min-w-[44px] items-center justify-center rounded-full bg-slateink text-sm font-extrabold text-white active:scale-95 overflow-hidden">
                {myPhoto?.has_photo
                  ? <img src={`${API}/profile/photo/${myPhoto.id}${myPhoto.photo_updated ? `?v=${encodeURIComponent(myPhoto.photo_updated)}` : ""}`} alt={user?.name || ""} className="h-full w-full object-cover" data-testid="profile-menu-photo" />
                  : initials}
              </button>
              {profileOpen && (
                <>
                <div className="fixed inset-0 z-40" onClick={() => setProfileOpen(false)} data-testid="profile-backdrop" />
                <div className="absolute right-0 top-12 z-50 w-64 rounded-2xl border border-slate-200 bg-white p-2 shadow-xl max-h-[calc(100dvh-72px)] overflow-y-auto overscroll-contain" data-testid="profile-menu" role="menu">
                  <div className="px-3 py-2">
                    <div className="text-sm font-bold text-slate-900 truncate">{user?.name}</div>
                    <div className="text-xs text-slate-500 truncate">{user?.email}</div>
                  </div>
                  <div className="my-1 border-t border-slate-100" />
                  <div className="flex items-center justify-between px-3 py-2">
                    <span className="text-xs font-semibold text-slate-600">{t("nav_language")}</span>
                    <LanguageToggle />
                  </div>
                  <div className="flex items-center justify-between px-3 py-2">
                    <span className="text-xs font-semibold text-slate-600">{t("field_mode")}</span>
                    <FieldModeToggle />
                  </div>
                  <div className="my-1 border-t border-slate-100" />
                  {!user?.is_demo && (user?.role === "owner" || user?.role === "admin" || user?.is_superadmin) && (
                    <button onClick={() => { setProfileOpen(false); enterDemo(); }} data-testid="profile-open-demo" role="menuitem"
                      className="flex w-full min-h-[44px] items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                      <HardHat className="h-4 w-4 text-slate-400" /> {t("demo_open")}
                    </button>
                  )}
                  {!user?.app_installed && (
                    <button onClick={() => { setProfileOpen(false); window.dispatchEvent(new Event("bf:show-install")); }} data-testid="profile-install-app" role="menuitem"
                      className="flex w-full min-h-[44px] items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                      <Camera className="h-4 w-4 text-slate-400" /> {t("install_app_menu")}
                    </button>
                  )}
                  <button onClick={doLogout} data-testid="btn-logout" role="menuitem"
                    className="flex w-full min-h-[44px] items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50">
                    <LogOut className="h-4 w-4" /> {t("action_signout")}
                  </button>
                  <button onClick={() => setProfileOpen(false)} data-testid="profile-close" role="menuitem"
                    className="flex w-full min-h-[44px] items-center justify-center rounded-xl px-3 py-2 text-sm font-bold text-slate-500 hover:bg-slate-50">
                    {t("close")}
                  </button>
                </div>
                </>
              )}
            </div>
          </div>
        </div>
        {/* Timezone mismatch warning: account tz vs device tz */}
      <TimezoneBanner user={user} />
      {/* Desktop nav */}
        <nav className="hidden xl:block border-t border-slate-100" data-testid="desktop-nav">
          <div className="mx-auto flex max-w-7xl items-center gap-1 px-4">
            {(fieldMode ? fieldItems : visibleMain).map((it) => it.camera ? (
              <button key="nav-camera" onClick={() => openCameraPicker(false)} data-testid="nav-camera-desktop" aria-label={t("nav_camera")}
                className="flex items-center gap-2 border-b-2 border-transparent px-4 py-3 text-sm font-semibold text-slate-500 transition-colors hover:text-slate-900">
                <Camera className="h-4 w-4" /> {t(it.key)}
              </button>
            ) : (
              <NavLink key={it.to} to={it.to} end={it.end} data-testid={`${it.testid}-desktop`}
                className={({ isActive }) =>
                  `flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-semibold transition-colors ${
                    isActive ? "border-brand text-brand" : "border-transparent text-slate-500 hover:text-slate-900"
                  }`
                }>
                <it.icon className="h-4 w-4" /> {t(it.key)}
              </NavLink>
            ))}
            {/* Desktop More dropdown */}
            <div className="relative" ref={deskMoreRef}>
              <button onClick={() => setDeskMoreOpen((v) => !v)} data-testid="nav-more-desktop"
                aria-expanded={deskMoreOpen} aria-label={t("nav_more")}
                className={`flex items-center gap-1.5 border-b-2 px-4 py-3 text-sm font-semibold transition-colors ${
                  moreActive ? "border-brand text-brand" : "border-transparent text-slate-500 hover:text-slate-900"
                }`}>
                <MoreHorizontal className="h-4 w-4" /> {t("nav_more")} <ChevronDown className="h-3 w-3" />
              </button>
              {deskMoreOpen && (
                <div className="absolute right-0 top-full z-50 mt-1 w-64 rounded-2xl border border-slate-200 bg-white p-3 shadow-xl max-h-[calc(100dvh-140px)] overflow-y-auto overscroll-contain" data-testid="desktop-more-menu" role="menu">
                  {(fieldMode ? moreGroups : moreGroups).filter((g) => g.items.some((it) => (!it.finOnly || user?.role !== "crew") && (!it.ownerOnly || user?.role !== "crew") && (!it.mod || modOn(it.mod)))).map((g) => (
                    <div key={g.key} className={g.hideInField && fieldMode ? "opacity-100" : ""}>
                      <div className="px-2 pt-2 pb-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">{t(g.key)}</div>
                      {g.items.filter((it) => (!it.finOnly || user?.role !== "crew") && (!it.ownerOnly || user?.role !== "crew") && (!it.mod || modOn(it.mod))).map((it) => (
                        <button key={it.to || it.testid} onClick={() => { setDeskMoreOpen(false); if (it.action === "assistant") openAssistant(); else navigate(it.to); }} data-testid={`${it.testid}-desktop`} role="menuitem"
                          className={`flex w-full min-h-[44px] items-center gap-2.5 rounded-xl px-3 py-2 text-sm font-semibold ${loc.pathname.startsWith(it.to) ? "bg-brand/10 text-brand" : "text-slate-700 hover:bg-slate-50"}`}>
                          <it.icon className="h-4 w-4 text-slate-400" /> {t(it.key)}
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </nav>
      </header>

      {/* Content */}
      <main className="relative z-10 mx-auto max-w-7xl px-4 py-6 md:py-10 bf-pb-safe xl:pb-16">
        {demoConfirm && createPortal(
        <div className="fixed inset-0 z-[95] flex items-end justify-center bg-black/50" onClick={() => setDemoConfirm(false)} data-testid="demo-reset-dialog">
          <div className="w-full max-w-lg rounded-t-3xl bg-white p-5 pb-[calc(20px+env(safe-area-inset-bottom,0px))] space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-base font-extrabold text-slate-900">{t("demo_reset")}</h3>
            <p className="text-sm text-slate-600">{t("demo_reset_confirm")}</p>
            <div className="flex gap-2">
              <button onClick={() => setDemoConfirm(false)} data-testid="demo-reset-cancel"
                className="min-h-[48px] flex-1 rounded-full border border-slate-300 bg-white text-sm font-bold text-slate-600">{t("action_cancel")}</button>
              <button onClick={resetDemo} disabled={demoResetting} data-testid="demo-reset-confirm"
                className="min-h-[48px] flex-1 rounded-full bg-violet-600 text-sm font-bold text-white active:scale-95 disabled:opacity-50">{t("demo_reset")}</button>
            </div>
          </div>
        </div>, document.body)}
      {user?.is_demo && (
          <div data-testid="demo-banner" className="mb-6 flex items-center gap-3 rounded-xl border border-violet-200 bg-violet-50 px-4 py-3 text-sm font-semibold text-violet-800">
            <HardHat className="h-4 w-4 shrink-0" /> {t("demo_banner")}
            <button onClick={() => setDemoConfirm(true)} disabled={demoResetting} data-testid="demo-reset-btn"
              className="ml-auto rounded-full border border-violet-300 bg-white px-4 py-1.5 text-xs font-bold text-violet-700 active:scale-95 disabled:opacity-50">
              {demoResetting ? "…" : t("demo_reset")}
            </button>
            <button onClick={exitDemo} data-testid="demo-back-btn"
              className="rounded-full bg-violet-600 px-4 py-1.5 text-xs font-bold text-white active:scale-95">{t("demo_back")}</button>
          </div>
        )}
        {trialDays != null && !billingAlert && (
          <div data-testid="trial-banner" className="mb-6 flex items-center gap-3 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm font-semibold text-sky-800">
            <CreditCard className="h-4 w-4 shrink-0" />
            <span>{t("trial_banner_left", { count: trialDays })}</span>
            <button onClick={() => navigate("/app/billing")} data-testid="trial-banner-cta" className="ml-auto rounded-full bg-brand px-4 py-1.5 text-xs font-bold text-white active:scale-95">{t("trial_banner_cta")}</button>
          </div>
        )}
        {billingAlert && (
          <div data-testid="billing-banner" className="mb-6 flex flex-wrap items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
            <AlertTriangle className="h-4 w-4 shrink-0" /> {billingAlert}
            <span className="flex gap-2 ml-auto">
              {billingCanRequest && !billingReqPending && (
                <button onClick={requestExtension} disabled={billingReqBusy} data-testid="billing-request-extension"
                  className="rounded-full border border-red-300 bg-white px-4 py-1.5 text-xs font-bold text-red-700 active:scale-95 disabled:opacity-60">
                  {billingReqBusy ? t("loading") : t("trial_request_btn")}
                </button>
              )}
              {billingReqPending && <span className="rounded-full bg-amber-100 px-3 py-1.5 text-xs font-bold text-amber-800" data-testid="billing-request-pending">{t("trial_request_pending")}</span>}
              <button onClick={() => navigate("/app/billing")} data-testid="billing-banner-cta" className="rounded-full bg-red-600 px-4 py-1.5 text-xs font-bold text-white active:scale-95">{t("billing_choose_plan")}</button>
            </span>
          </div>
        )}
        <CcAnnouncementBanner />
        {children}
      </main>

      {/* Mobile bottom nav: Home/My Day · Projects · Photos · Schedule · More (fixed above safe area). Hidden during the New Project wizard so it can't cover form buttons. */}
      {!loc.pathname.startsWith("/app/projects/new") && (
      <nav className={`fixed bottom-0 left-0 right-0 z-40 xl:hidden border-t border-slate-200 bg-white ${anyModal ? "hidden" : ""}`} data-testid="mobile-nav">
        <div className={`mx-auto grid max-w-lg px-1 pb-[env(safe-area-inset-bottom,0px)] ${fieldMode ? "text-[11px]" : ""}`}
          style={{ gridTemplateColumns: `repeat(${showMoneyNav ? 7 : 6}, minmax(0, 1fr))` }}>
          {(fieldMode
            ? [{ to: "/app/time", key: "nav_my_day", icon: Clock, testid: "nav-my-day", end: true }]
            : [{ to: "/app", key: "nav_home", icon: Home, testid: "nav-home", end: true }]
          ).concat([
            { to: "/app/projects", key: "nav_projects", icon: HardHat, testid: "nav-projects" },
          ]).map((it) => (
            <NavLink key={it.to} to={it.to} end={it.end} data-testid={it.testid}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 ${fieldMode ? "py-3.5" : "py-3"} min-h-[52px] text-[10px] font-semibold ${isActive ? "text-brand" : "text-slate-400"}`
              }>
              <it.icon className={fieldMode ? "h-6 w-6" : "h-5 w-5"} />
              <span>{t(it.key)}</span>
            </NavLink>
          ))}
          {/* Big + — one way to add anything (Batch 1). Long-press = Camera. */}
          <button
            onPointerDown={(e) => {
              e.currentTarget._lp = setTimeout(() => { e.currentTarget._lpFired = true; openCameraPicker(false); }, 500);
            }}
            onPointerUp={(e) => clearTimeout(e.currentTarget._lp)}
            onPointerLeave={(e) => clearTimeout(e.currentTarget._lp)}
            onClick={(e) => {
              // Real taps AND keyboard/programmatic clicks open the sheet;
              // a long-press (pointer timer fired) opens the camera instead.
              if (e.currentTarget._lpFired) { e.currentTarget._lpFired = false; return; }
              setQuickAdd(true);
            }}
            data-testid="nav-add" aria-label={t("qa_title")}
            className="flex flex-col items-center justify-start gap-0.5 min-h-[52px] text-[10px] font-semibold text-brand">
            <span className="-mt-5 flex h-14 w-14 items-center justify-center rounded-full bg-brand text-white shadow-lg ring-4 ring-white transition-transform active:scale-90">
              <Plus className="h-7 w-7" />
            </span>
            <span>{t("qa_add")}</span>
          </button>
          {[
            { to: "/app/photos", key: "nav_photos", icon: Images, testid: "nav-photos" },
            { to: "/app/schedule", key: "nav_schedule", icon: Calendar, testid: "nav-schedule" },
          ].concat(showMoneyNav ? [{ to: "/app/financials", key: "nav_financials", icon: FileCheck, testid: "nav-money-mobile" }] : []).map((it) => (
            <NavLink key={it.to} to={it.to} end={it.end} data-testid={it.testid}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 ${fieldMode ? "py-3.5" : "py-3"} min-h-[52px] text-[10px] font-semibold ${isActive ? "text-brand" : "text-slate-400"}`
              }>
              <it.icon className={fieldMode ? "h-6 w-6" : "h-5 w-5"} />
              <span>{t(it.key)}</span>
            </NavLink>
          ))}
          <button onClick={() => setMoreOpen(true)} data-testid="nav-more" aria-label={t("nav_more")}
            className={`flex flex-col items-center justify-center gap-1 ${fieldMode ? "py-3.5" : "py-3"} min-h-[52px] text-[10px] font-semibold ${moreActive ? "text-brand" : "text-slate-400"}`}>
            <MoreHorizontal className={fieldMode ? "h-6 w-6" : "h-5 w-5"} />
            <span>{t("nav_more")}</span>
          </button>
        </div>
      </nav>
      )}

      {/* More sheet (mobile) — grouped sections; nothing removed in Field Mode */}
      {moreOpen && (
        <div className="fixed inset-0 z-[65] xl:hidden" data-testid="more-sheet" role="dialog" aria-modal="true" aria-label={t("nav_more")}>
          <div className="absolute inset-0 bg-black/60" onClick={() => setMoreOpen(false)} data-testid="more-backdrop" />
          <div className="absolute inset-x-0 bottom-0 rounded-t-3xl bg-white border-t border-slate-200 p-4 pb-[max(env(safe-area-inset-bottom,8px),16px)] max-h-[80vh] overflow-y-auto">
            <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-slate-300" />
            <div className="px-1 pb-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">{t("nav_group_work")}</div>
            <div className="grid grid-cols-3 gap-2">
              {mobileWorkItems.filter((it) => (!it.finOnly || user?.role !== "crew") && (!it.mod || modOn(it.mod))).map((it) => (
                <button key={it.to} onClick={() => { setMoreOpen(false); navigate(it.to); }} data-testid={it.testid}
                  className={`flex flex-col items-center justify-center gap-2 rounded-2xl border p-4 min-h-[64px] ${loc.pathname.startsWith(it.to) ? "border-brand bg-brand/10 text-brand" : "border-slate-200 bg-slate-50 text-slate-700"}`}>
                  <it.icon className="h-5 w-5 text-brand" />
                  <span className="text-xs font-semibold">{t(it.key)}</span>
                </button>
              ))}
            </div>
            {moreGroups.filter((g) => g.items.some((it) => !it.desktopOnly && (!it.finOnly || user?.role !== "crew") && (!it.ownerOnly || user?.role !== "crew") && (!it.mod || modOn(it.mod)))).map((g) => (
              <div key={g.key}>
                <div className="px-1 pt-3 pb-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">{t(g.key)}</div>
                <div className="grid grid-cols-3 gap-2">
                  {g.items.filter((it) => !it.desktopOnly && (!it.finOnly || user?.role !== "crew") && (!it.ownerOnly || user?.role !== "crew") && (!it.mod || modOn(it.mod))).map((it) => (
                    <button key={it.to || it.testid} onClick={() => { setMoreOpen(false); if (it.action === "assistant") openAssistant(); else navigate(it.to); }} data-testid={it.testid}
                      className={`flex flex-col items-center justify-center gap-2 rounded-2xl border p-4 min-h-[64px] ${it.to && loc.pathname.startsWith(it.to) ? "border-brand bg-brand/10 text-brand" : "border-slate-200 bg-slate-50 text-slate-700"}`}>
                      <it.icon className="h-5 w-5 text-brand" />
                      <span className="text-xs font-semibold">{t(it.key)}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <div className="mt-3 flex items-center justify-between gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              <span className="text-xs font-semibold text-slate-600">{t("nav_account")}</span>
              <div className="flex items-center gap-2">
                <LanguageToggle />
                <button onClick={doLogout} data-testid="more-signout"
                  className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 min-h-[44px]">
                  <LogOut className="h-3.5 w-3.5" /> {t("action_signout")}
                </button>
              </div>
            </div>
            <button onClick={() => setMoreOpen(false)} data-testid="more-close"
              className="mt-3 flex w-full min-h-[44px] items-center justify-center rounded-2xl border border-slate-200 bg-white text-sm font-bold text-slate-500 active:scale-[0.99]">
              {t("close")}
            </button>
          </div>
        </div>
      )}

      {/* Camera project picker (all viewports — Camera is a primary nav action) */}
      {camPickerOpen && (
        <div className="fixed inset-0 z-[92]" data-testid="camera-sheet" role="dialog" aria-modal="true" aria-label={t("cam_choose_project")}>
          <div className="absolute inset-0 bg-black/60" onClick={() => setCamPickerOpen(false)} data-testid="camera-sheet-backdrop" />
          <div className="absolute inset-x-0 bottom-0 rounded-t-3xl bg-white border-t border-slate-200 p-4 pb-[max(env(safe-area-inset-bottom,8px),16px)] max-h-[60vh] overflow-y-auto">
            <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-slate-300" />
            <div className="text-sm font-bold text-slate-900 mb-3 px-1">{t("cam_choose_project")}</div>
            <input value={camSearch} onChange={(e) => setCamSearch(e.target.value)} placeholder={t("cam_search", "Search projects")}
              data-testid="cam-search" aria-label={t("cam_search", "Search projects")}
              className="mb-2 h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900" />
            <Link to="/app/projects/new" onClick={() => setCamPickerOpen(false)} data-testid="cam-new-project"
              className="mb-2 flex min-h-[48px] w-full items-center gap-2 rounded-xl border border-dashed border-brand/50 bg-brand/5 px-4 text-sm font-bold text-brand active:scale-[0.98]">
              + {t("cam_new_project", "New project")}
            </Link>
            {camLoading && (
              <div className="flex items-center justify-center gap-2 px-1 py-4" data-testid="cam-picker-loading" role="status">
                <RefreshCw className="h-4 w-4 animate-spin text-brand" />
                <span className="text-xs font-semibold text-slate-500">{t("loading")}</span>
              </div>
            )}
            {!camLoading && camProjects.length === 0 && <div className="text-xs text-slate-500 px-1 py-4">{t("photos_select_project")}…</div>}
            {camProjects.filter((p) => !camSearch.trim() || (p.name || "").toLowerCase().includes(camSearch.trim().toLowerCase()) || (p.address || "").toLowerCase().includes(camSearch.trim().toLowerCase())).map((p) => (
              <button key={p.id} onClick={() => { setCamPickerOpen(false); setCamProject(p.id); setCamProjectName(p.name || ""); try { localStorage.setItem("buildflow.camera.lastProject", p.id); const r = JSON.parse(localStorage.getItem("buildflow.camera.recentProjects") || "[]").filter((x) => x !== p.id); r.unshift(p.id); localStorage.setItem("buildflow.camera.recentProjects", JSON.stringify(r.slice(0, 10))); } catch { /* ignore */ } }} data-testid={`cam-pick-${p.id}`}
                className="w-full text-left rounded-xl border border-slate-200 bg-slate-50 px-4 py-4 mb-2 text-sm font-semibold text-slate-900 active:scale-[0.98] min-h-[48px]">
                <span className="flex items-center gap-2">
                  <span className="min-w-0 flex-1 truncate">{p.name}</span>
                  {p.id === camTodayId && <span className="shrink-0 rounded-full bg-brand/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-brand" data-testid="cam-today-badge">{t("sched_today")}</span>}
                  {camNearIds.has(p.id) && <span className="shrink-0 rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-green-700" data-testid={`cam-near-${p.id}`}>{t("cam_near", "Near you")}</span>}
                </span>
                {p.address && <span className="block text-[11px] font-normal text-slate-500 truncate">{p.address}</span>}
              </button>
            ))}
            <button onClick={() => setCamPickerOpen(false)} data-testid="cam-picker-cancel"
              className="mt-1 flex w-full min-h-[48px] items-center justify-center rounded-xl border border-slate-200 bg-white text-sm font-bold text-slate-500 active:scale-[0.99]">
              {t("action_cancel")}
            </button>
          </div>
        </div>
      )}

      {/* Global offline indicator (Phase 6) */}
      <UploadQueuePill />
      <StuckQueueBanner />
      {isOffline && (
        <div className="fixed inset-x-0 top-0 z-[80] bg-amber-500 px-4 py-1.5 text-center text-[11px] font-bold text-amber-950" data-testid="offline-banner" role="status">
          {t("app_offline_banner")}
        </div>
      )}

      <CameraCapture projectId={camProject} projectName={camProjectName || (camProjects.find((p) => p.id === camProject) || {}).name || ""}
        initialMode={camMode}
        open={!!camProject} onClose={() => { setCamProject(null); setCamMode("photo"); }} onUploaded={() => {}}
        onFinish={() => navigate(camReturnTo.current || "/app/photos")} />

      {fileView && <FileViewer {...fileView} onClose={() => setFileView(null)} />}
      <DiscardConfirmHost />

      {asstMounted && <AssistantPanel open={asstOpen} onClose={() => setAsstOpen(false)} init={asstInit} />}
      <InstallPrompt />
      <PushPrompt />
      <QuickAddSheet open={quickAdd} onClose={() => setQuickAdd(false)}
        onCamera={() => openCameraPicker(false)} modOn={modOn} />
      <UpdateToast />
    </div>
  );
}

/* Round 13 #1: app-styled discard confirmation for dirty forms (replaces native confirm). */
function DiscardConfirmHost() {
  const { t } = useTranslation();
  const [resolver, setResolver] = useState(null);
  useEffect(() => {
    setDiscardHandler(() => new Promise((resolve) => setResolver(() => resolve)));
    return () => setDiscardHandler(null);
  }, []);
  if (!resolver) return null;
  const answer = (v) => { const r = resolver; setResolver(null); r(v); };
  return createPortal(
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 p-6 pointer-events-auto" style={{ pointerEvents: "auto" }} data-testid="discard-confirm" role="alertdialog" aria-modal="true" aria-label={t("discard_title")}>
      <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl">
        <h3 className="text-base font-extrabold text-slate-900">{t("discard_title")}</h3>
        <div className="mt-4 flex gap-2">
          <button onClick={() => answer(false)} data-testid="discard-keep"
            className="min-h-[48px] flex-1 rounded-full border border-slate-300 text-sm font-bold text-slate-700 active:scale-95">{t("discard_keep")}</button>
          <button onClick={() => answer(true)} data-testid="discard-go"
            className="min-h-[48px] flex-1 rounded-full bg-red-600 text-sm font-bold text-white active:scale-95">{t("discard_go")}</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function TimezoneBanner({ user }) {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem("bp_tz_dismissed") === "1"; } catch { return false; }
  });
  const [busy, setBusy] = useState(false);
  const [sugg, setSugg] = useState(null);
  useEffect(() => {
    let live = true;
    api.get("/profile/tz-suggestion").then(({ data }) => { if (live) setSugg(data); }).catch(() => {});
    return () => { live = false; };
  }, []);
  const nav = useNavigate();
  if (!user || dismissed || !sugg || !sugg.mismatch) return null;
  const change = async () => {
    setBusy(true);
    try {
      await api.post("/auth/timezone", { tz: sugg.suggested });
      toast.success(t("tz_banner_updated"));
      window.location.reload();
    } catch (e) { toast.error(t("toast_failed")); }
    setBusy(false);
  };
  return (
    <div className="border-b border-amber-200 bg-amber-50 px-4 py-2.5" data-testid="tz-banner" role="alert">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-2">
        <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
        <p className="flex-1 text-xs font-semibold text-amber-900 min-w-[200px]" data-testid="tz-banner-text">
          {t("tz_banner_company", { current: sugg.current_region, suggested: sugg.suggested_region })}
          <span className="block text-[11px] font-normal text-amber-700">{t("tz_banner_note")}</span>
        </p>
        <button onClick={change} disabled={busy} data-testid="tz-banner-change"
          className="rounded-full bg-brand px-3 py-1.5 text-xs font-bold text-white active:scale-95 disabled:opacity-60">
          {t("tz_banner_change", { tz: sugg.suggested_region })}
        </button>
        <button onClick={() => nav("/app/profile")} data-testid="tz-banner-choose"
          className="rounded-full border border-amber-300 px-3 py-1.5 text-xs font-bold text-amber-800 hover:bg-amber-100">
          {t("tz_banner_choose")}
        </button>
        <button onClick={() => { try { localStorage.setItem("bp_tz_dismissed", "1"); } catch {} setDismissed(true); }} data-testid="tz-banner-dismiss"
          className="rounded-full px-3 py-1.5 text-xs font-bold text-amber-800 hover:bg-amber-100">
          {t("tz_banner_keep", { tz: sugg.current_region })}
        </button>
      </div>
    </div>
  );
}

// Fast Photos (Wave 2): company owner/admin banner — "N photos haven't
// uploaded from X's phone for Y min" while any device queue is stuck 30+ min.
function StuckQueueBanner() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [stuck, setStuck] = useState([]);
  useEffect(() => {
    if (!user || !["owner", "admin"].includes(user.role)) return undefined;
    let alive = true;
    const load = () => api.get("/photos/upload-health")
      .then(({ data }) => { if (alive) setStuck((data.devices || []).filter((d) => (d.oldest_min || 0) >= 30)); })
      .catch(() => {});
    load();
    const iv = setInterval(load, 5 * 60 * 1000);
    return () => { alive = false; clearInterval(iv); };
  }, [user && user.role]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!stuck.length) return null;
  const d = stuck[0];
  return (
    <Link to="/app/photos" data-testid="queue-stuck-banner" role="alert"
      className="fixed inset-x-0 top-0 z-[85] bg-amber-500 px-4 py-2 text-center text-xs font-bold text-amber-950 shadow">
      {t("queue_stuck_banner", { count: d.pending, device: d.name || t("queue_stuck_device"), min: d.oldest_min })}
    </Link>
  );
}

